// Keyframes this tab is editing, so a read that started before an edit, or ran while it saved, cannot overwrite it
import type { Keyframe } from './types';

export interface Pose {
  position?: { x: number; y: number; z: number };
  rotation?: { x: number; y: number; z: number };
  scale?: { x: number; y: number; z: number };
}

let clock = 0;
const lastStart = new Map<string, number>();
const lastFinish = new Map<string, number>();
const saving = new Map<string, number>();
const deleted = new Set<string>();
const reportedPose = new Map<string, Pose>();
const confirmed = new Map<string, { keyframe: Keyframe; at: number }>();
const stated = new Map<string, { keyframe: Keyframe; at: number }>();
// A placement report later than this is the renderer's own, not an answer to the pose the editor stated
const STATED_POSE_MS = 30000;
const queue = new Map<string, Promise<void>>();

export const readStarted = (): number => clock;

export function changedSince(keyframeId: string, readAt: number): boolean {
  return Math.max(lastStart.get(keyframeId) ?? 0, lastFinish.get(keyframeId) ?? 0) > readAt || saving.has(keyframeId);
}

export function newerEditThan(keyframeId: string, tick: number): boolean {
  return (lastStart.get(keyframeId) ?? 0) > tick;
}

// The renderer's own placement (byUser false) guards reads like any save but is not an edit newerEditThan sees.
// Saves of a keyframe go out one after another, so the server holds the one that started last and confirmations
// follow the order it applied them
export async function savingKeyframes<T>(keyframeIds: string[], save: () => Promise<T>, { byUser = true } = {}): Promise<{ result: T; tick: number }> {
  const tick = ++clock;
  const earlier = keyframeIds.map(id => queue.get(id)).filter((p): p is Promise<void> => !!p);
  const run = Promise.all(earlier).then(save);
  const settled = run.then(() => undefined, () => undefined);
  for (const id of keyframeIds) {
    if (byUser) lastStart.set(id, tick);
    saving.set(id, (saving.get(id) ?? 0) + 1);
    queue.set(id, settled);
  }
  try {
    return { result: await run, tick };
  } finally {
    const done = ++clock;
    for (const id of keyframeIds) {
      lastFinish.set(id, done);
      const left = (saving.get(id) ?? 1) - 1;
      if (left > 0) saving.set(id, left);
      else saving.delete(id);
      if (queue.get(id) === settled) queue.delete(id);
    }
  }
}

export function deletedHere(keyframeId: string): void {
  deleted.add(keyframeId);
}

export const wasDeletedHere = (keyframeId: string): boolean => deleted.has(keyframeId);

export function poseReported(keyframeId: string, pose: Pose): void {
  reportedPose.set(keyframeId, pose);
}

export function forgetReportedPose(keyframeId: string): void {
  reportedPose.delete(keyframeId);
}

export const lastReportedPose = (keyframeId: string): Pose | undefined => reportedPose.get(keyframeId);

// What the server last held for a keyframe: a failed save goes back to this, not to the edit before it, which may be unsaved too.
// `at` is when the save started or the read began, so a save that finishes late cannot replace a newer one
export function confirmedAs(keyframe: Keyframe, at: number = clock): void {
  const was = confirmed.get(keyframe.id);
  if (!was || at >= was.at) confirmed.set(keyframe.id, { keyframe, at });
}

export const lastConfirmed = (keyframeId: string): Keyframe | undefined => confirmed.get(keyframeId)?.keyframe;

export function poseStated(keyframe: Keyframe): void {
  stated.set(keyframe.id, { keyframe, at: Date.now() });
}

export function takeStatedPose(keyframeId: string): Keyframe | undefined {
  const entry = stated.get(keyframeId);
  stated.delete(keyframeId);
  return entry && Date.now() - entry.at < STATED_POSE_MS ? entry.keyframe : undefined;
}
